import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import { v4 as uuid } from "uuid";

const tableName = "project-activity";

/**
 * Log a project activity event.
 *
 * @param {Object} params
 * @param {string} params.projectId      - The project ID
 * @param {string} params.communityCode - Community code
 * @param {string} params.userId        - UID of the actor
 * @param {string} params.userName      - Display name of the actor
 * @param {string} params.type          - Event type (e.g. "status_change", "work_log", "classified", "disbursement")
 * @param {string} params.description   - Human-readable description
 * @param {Object} [params.meta]        - Optional structured metadata
 */
export const logProjectActivity = ({
  projectId,
  communityCode,
  userId,
  userName,
  type,
  description,
  meta = {},
}) => {
  if (!projectId || !communityCode) return Promise.resolve();
  const id = uuid();
  return setDoc(doc(db, tableName, id), {
    id,
    projectId,
    communityCode,
    userId,
    userName,
    type,
    description,
    meta,
    timestamp: Timestamp.now(),
  });
};

/**
 * Subscribe to activity events for a project in real-time, ordered newest first.
 *
 * @param {string} projectId - The project ID
 * @param {Function} cb     - Callback receiving an array of activity objects
 * @returns {Function}       Unsubscribe function
 */
export const getProjectActivityInRealTime = (projectId, cb) => {
  if (!projectId) { cb([]); return () => {}; }
  return onSnapshot(
    query(
      collection(db, tableName),
      where("projectId", "==", projectId),
      orderBy("timestamp", "desc")
    ),
    (snap) => {
      const items = [];
      snap.forEach((each) => items.push(each.data()));
      cb(items);
    },
    (err) => {
      // If index not ready, fall back to unordered
      console.warn("project-activity query error (index may be pending):", err);
      const unsub2 = onSnapshot(
        query(
          collection(db, tableName),
          where("projectId", "==", projectId)
        ),
        (snap) => {
          const items = [];
          snap.forEach((each) => items.push(each.data()));
          items.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
          cb(items);
        }
      );
      // Replace this listener with the fallback
      return unsub2;
    }
  );
};
